/**
 * /en/practice — English practice-area hub.
 *
 * Hand-authored English content from src/data/practice/en-a.ts and en-b.ts.
 * No machine translation and no AI call at build or request time.
 * No fees, no outcome guarantees, no unverifiable statistics.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { absoluteAsset, absoluteUrl } from "@/config/sites";
import { canonicalAndHreflang } from "@/lib/hreflang";
import { EN_PRACTICE_LIST } from "@/data/practice/en";
import { FirmCta } from "@/components/firm/FirmCta";
import { OfficialSourcesEn } from "@/components/OfficialSourcesEn";
import { ContentFreshness } from "@/components/ContentFreshness";
import teamAsset from "@/assets/team-group.png.asset.json";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  legalServiceSchema,
  faqPageSchema,
} from "@/lib/schema";

const TITLE = "Practice Areas — Litigation, Corporate, Family, Estate, Property, Tax";
const DESC =
  "Ten practice areas of a full-service Thai law firm: court litigation and enforcement, corporate and contracts, family, estate, land and property, employment, tax, intellectual property, PDPA and international document work. Scope, documents and real working steps for each.";

const HUB_FAQ = [
  {
    question: "Our situation touches several practice areas. Do we need separate engagements?",
    answer:
      "No. The firm opens one file with one lawyer in charge of coordination. A cross-border estate with Thai land and heirs living abroad, for example, involves probate work, land-office work and international document certification inside a single file, so documents are collected once and nothing is lost between teams.",
  },
  {
    question: "Do we need every document ready before the first meeting?",
    answer:
      "No. Send what you already have and the lawyer will review it first, then tell you which documents are still missing, which office issues each one, and which need certified translation or legalisation before a Thai court or agency will accept them.",
  },
  {
    question: "Can the firm act for clients living outside Thailand?",
    answer:
      "Yes. Initial assessment works from scans sent by email or LINE, and filings are made with the court or agency that has jurisdiction. Where Thai law requires the client to sign personally or appear in person, the lawyer explains the scheduling options and the form of power of attorney the receiving office accepts.",
  },
  {
    question: "Which language does the firm work in?",
    answer:
      "Instructions, advice and correspondence can be handled in English, while court filings and documents submitted to Thai agencies are prepared in Thai as those bodies require. Where a document must be relied on in both languages, the firm arranges certified translation so the Thai and English versions match.",
  },
];

export const Route = createFileRoute("/en/practice/")({
  component: EnPracticeHub,
  loader: () => ({
    h: (() => {
      const url = absoluteUrl("/en/practice", null, "en");
      const ogImage = absoluteAsset(teamAsset.url);
      return {
        meta: [
          { title: TITLE },
          { name: "description", content: DESC },
          { property: "og:title", content: TITLE },
          { property: "og:description", content: DESC },
          { property: "og:url", content: url },
          { property: "og:type", content: "website" },
          { property: "og:image", content: ogImage },
          { property: "og:image:alt", content: "Lawyers of Thai Notary Law & Service" },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:image", content: ogImage },
        ],
        links: [
          { rel: "canonical", href: url },
          ...canonicalAndHreflang("/practice", null, "en").filter((l) => l.rel === "alternate"),
        ],
        scripts: [
          jsonLdScript(
            buildJsonLd([
              webPageSchema({ url: "/en/practice", title: TITLE, description: DESC }),
              breadcrumbListSchema([
                { name: "Home", url: absoluteUrl("/en", null, "en") },
                { name: "Practice areas", url },
              ]),
              ...EN_PRACTICE_LIST.map((a) =>
                legalServiceSchema({
                  name: a.name,
                  description: a.tagline,
                  url: `/en/practice/${a.slug}`,
                  areaServed: "TH",
                  serviceType: a.name,
                }),
              ),
              faqPageSchema(HUB_FAQ),
            ]),
          ),
        ],
      };
    })(),
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

function EnPracticeHub() {
  return (
    <main>
      <header
        className="border-b border-border/60 text-[var(--primary-foreground)]"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="mx-auto max-w-6xl px-6 py-14">
          <nav aria-label="Breadcrumb" className="text-xs text-[var(--primary-foreground)]/70">
            <Link to="/en" className="hover:underline">
              Home
            </Link>{" "}
            <span aria-hidden>/</span> Practice areas
          </nav>
          <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight md:text-5xl">
            Practice areas of the firm
          </h1>
          <p className="mt-5 max-w-3xl leading-relaxed text-[var(--primary-foreground)]/85">
            Thai Notary Law &amp; Service handles {EN_PRACTICE_LIST.length} practice areas through lawyers licensed by
            the Lawyers Council of Thailand. Each section below states what the firm actually does, the matters clients
            bring, the documents worth collecting first and the pitfalls we see most often, so you can judge the work
            before deciding.
          </p>
          <ul className="mt-8 flex flex-wrap gap-2">
            {EN_PRACTICE_LIST.map((a) => (
              <li key={a.slug}>
                <Link
                  to="/en/practice/$area"
                  params={{ area: a.slug }}
                  className="inline-block rounded-full border border-[var(--gold)]/40 px-3 py-1 text-xs font-medium hover:bg-[var(--primary-foreground)]/10"
                >
                  {a.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6">
        {EN_PRACTICE_LIST.map((a) => (
          <section key={a.slug} id={a.slug} className="scroll-mt-24 border-b border-border/60 py-14">
            <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
              <Link to="/en/practice/$area" params={{ area: a.slug }} className="hover:underline">
                {a.name}
              </Link>
            </h2>
            <p className="mt-2 text-sm font-semibold text-[var(--gold)]">{a.tagline}</p>
            <p data-speakable className="answer-summary mt-4 leading-relaxed text-muted-foreground">
              {a.answer}
            </p>
            <p className="mt-3 text-sm font-semibold">
              <Link to="/en/practice/$area" params={{ area: a.slug }} className="text-primary hover:underline">
                Read the full {a.name} page — documents, steps, comparison and FAQs →
              </Link>
            </p>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="text-base font-semibold text-foreground">What the firm handles</h3>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {a.scope.map((s) => (
                    <li key={s} className="flex gap-2">
                      <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Matters clients bring us</h3>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {a.examples.map((s) => (
                    <li key={s} className="flex gap-2">
                      <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 rounded-xl border border-border bg-card p-6">
              <h3 className="text-base font-semibold text-foreground">Documents to collect first</h3>
              <ul className="mt-3 grid gap-2 text-sm leading-relaxed text-muted-foreground sm:grid-cols-2">
                {a.docs.map((s) => (
                  <li key={s} className="flex gap-2">
                    <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-muted-foreground">
                Missing documents are normal at this stage. Send what you have and the lawyer will identify the issuing
                office for the rest.
              </p>
            </div>

            <div className="mt-8">
              <h3 className="text-base font-semibold text-foreground">How the work proceeds</h3>
              <ol className="mt-3 space-y-3">
                {a.steps.map((s, i) => (
                  <li key={s} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--primary)] text-xs font-bold text-[var(--gold)]">
                      {i + 1}
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-8">
              <h3 className="text-base font-semibold text-foreground">Pitfalls to avoid</h3>
              <div className="mt-3 space-y-3">
                {a.cautions.map((c) => (
                  <div key={c.title} className="rounded-lg border-l-4 border-[var(--gold)] bg-muted/50 p-4">
                    <p className="text-sm font-semibold text-foreground">{c.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 rounded-xl border border-[var(--gold)]/40 bg-muted/40 p-6">
              <h3 className="text-base font-semibold text-foreground">Hand the whole matter to the firm</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.handoff}</p>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm font-semibold">
                <Link to="/en/contact" className="text-primary hover:underline">
                  Talk to a lawyer about this →
                </Link>
                <Link to="/practice/$area" params={{ area: a.slug }} className="text-primary hover:underline">
                  อ่านหน้านี้เป็นภาษาไทย →
                </Link>
              </div>
            </div>
          </section>
        ))}
      </div>

      <section aria-labelledby="practice-faq" className="mx-auto max-w-4xl px-6 py-16">
        <h2 id="practice-faq" className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
          Working with a Thai law firm: frequently asked questions
        </h2>
        <dl className="mt-8 space-y-6">
          {HUB_FAQ.map((f) => (
            <div key={f.question} className="rounded-xl border border-border bg-card p-6">
              <dt className="text-base font-semibold text-foreground">{f.question}</dt>
              <dd className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{f.answer}</dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="mx-auto max-w-4xl px-6 pb-16">
        <OfficialSourcesEn theme="law" />
        <ContentFreshness path="/en/practice" locale="en" reviewer="Thai lawyer, Lawyers Council of Thailand" />
      </div>

      <FirmCta heading="Speak with the lawyer responsible for your matter" />
    </main>
  );
}
